import { 
  collection, 
  doc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where,
  writeBatch,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig.jsx';

/**
 * 사용자 완전 삭제 서비스
 * 비자 만료된 사용자의 모든 데이터를 안전하게 삭제합니다.
 */
class UserDeletionService {
  constructor() {
    this.appId = 'kairos-platform-1ffde';
    this.batchSize = 500; // Firestore 배치 제한
  }

  /**
   * 사용자 완전 삭제
   * @param {string} userId - 삭제할 사용자 ID
   * @returns {Object} 삭제 결과
   */
  async deleteUserCompletely(userId) {
    try {
      console.log('🗑️ 사용자 완전 삭제 시작:', userId);
      
      if (!userId) {
        throw new Error('사용자 ID가 필요합니다.');
      }

      const deletionResults = {
        userId: userId,
        deletedCollections: [],
        totalDeleted: 0,
        errors: []
      };

      // 1. 사용자 메인 문서 삭제
      await this.deleteUserMainDocuments(userId, deletionResults);

      // 2. 사용자 서브컬렉션들 삭제
      await this.deleteUserSubcollections(userId, deletionResults);

      // 3. 관련 데이터 삭제 (메시지, 알림 등)
      await this.deleteRelatedData(userId, deletionResults);

      console.log('✅ 사용자 완전 삭제 완료:', deletionResults);
      return {
        success: true,
        message: `사용자 ${userId}의 모든 데이터가 삭제되었습니다.`,
        details: deletionResults
      };

    } catch (error) {
      console.error('❌ 사용자 삭제 실패:', error);
      return {
        success: false,
        error: error.message,
        message: `사용자 삭제 중 오류가 발생했습니다: ${error.message}`
      };
    }
  }

  /**
   * 사용자 메인 문서들 삭제
   */
  async deleteUserMainDocuments(userId, deletionResults) {
    const mainCollections = [
      `artifacts/${this.appId}/users/${userId}`,
      'users',
      'userProfiles'
    ];

    for (const collectionPath of mainCollections) {
      try {
        const docRef = doc(db, collectionPath, userId);
        await deleteDoc(docRef);
        deletionResults.deletedCollections.push(collectionPath);
        deletionResults.totalDeleted++;
        console.log(`✅ 메인 문서 삭제: ${collectionPath}/${userId}`);
      } catch (error) {
        if (error.code !== 'not-found') {
          deletionResults.errors.push(`${collectionPath}: ${error.message}`);
          console.warn(`⚠️ 메인 문서 삭제 실패: ${collectionPath}/${userId}`, error.message);
        }
      }
    }
  }

  /**
   * 사용자 서브컬렉션들 삭제
   */
  async deleteUserSubcollections(userId, deletionResults) {
    const subcollections = [
      'documents',
      'messages',
      'notifications',
      'remittances',
      'kyc',
      'settings',
      'activities'
    ];

    for (const subcollection of subcollections) {
      try {
        const collectionPath = `artifacts/${this.appId}/users/${userId}/${subcollection}`;
        const deletedCount = await this.deleteCollection(collectionPath);
        
        if (deletedCount > 0) {
          deletionResults.deletedCollections.push(collectionPath);
          deletionResults.totalDeleted += deletedCount;
          console.log(`✅ 서브컬렉션 삭제: ${collectionPath} (${deletedCount}개 문서)`);
        }
      } catch (error) {
        deletionResults.errors.push(`${subcollection}: ${error.message}`);
        console.warn(`⚠️ 서브컬렉션 삭제 실패: ${subcollection}`, error.message);
      }
    }
  }

  /**
   * 관련 데이터 삭제 (메시지, 알림 등)
   */
  async deleteRelatedData(userId, deletionResults) {
    try {
      // 사용자 이메일로 메시지 삭제
      const userDoc = await this.getUserEmail(userId);
      if (userDoc && userDoc.email) {
        await this.deleteUserMessages(userDoc.email, deletionResults);
        await this.deleteUserNotifications(userId, deletionResults);
      }
    } catch (error) {
      deletionResults.errors.push(`관련 데이터: ${error.message}`);
      console.warn('⚠️ 관련 데이터 삭제 실패:', error.message);
    }
  }

  /**
   * 사용자 이메일 조회
   */
  async getUserEmail(userId) {
    try {
      const userRef = doc(db, `artifacts/${this.appId}/users/${userId}`);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        return userSnap.data();
      }
    } catch (error) {
      console.warn('사용자 이메일 조회 실패:', error.message);
    }
    return null;
  }

  /**
   * 사용자 메시지 삭제
   */
  async deleteUserMessages(userEmail, deletionResults) {
    try {
      const messagesRef = collection(db, 'privateMessages');
      const q = query(
        messagesRef,
        where('recipientId', '==', userEmail.toLowerCase())
      );
      
      const snapshot = await getDocs(q);
      const batch = writeBatch(db);
      let count = 0;

      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
        count++;
      });

      if (count > 0) {
        await batch.commit();
        deletionResults.deletedCollections.push(`privateMessages (${count}개)`);
        deletionResults.totalDeleted += count;
        console.log(`✅ 사용자 메시지 삭제: ${count}개`);
      }
    } catch (error) {
      deletionResults.errors.push(`메시지: ${error.message}`);
      console.warn('⚠️ 메시지 삭제 실패:', error.message);
    }
  }

  /**
   * 사용자 알림 삭제
   */
  async deleteUserNotifications(userId, deletionResults) {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId)
      );
      
      const snapshot = await getDocs(q);
      const batch = writeBatch(db);
      let count = 0;

      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
        count++;
      });

      if (count > 0) {
        await batch.commit();
        deletionResults.deletedCollections.push(`notifications (${count}개)`);
        deletionResults.totalDeleted += count;
        console.log(`✅ 사용자 알림 삭제: ${count}개`);
      }
    } catch (error) {
      deletionResults.errors.push(`알림: ${error.message}`);
      console.warn('⚠️ 알림 삭제 실패:', error.message);
    }
  }

  /**
   * 컬렉션 전체 삭제 (배치 처리)
   */
  async deleteCollection(collectionPath) {
    let deletedCount = 0;
    let hasMore = true;

    while (hasMore) {
      const collectionRef = collection(db, collectionPath);
      const q = query(collectionRef, limit(this.batchSize));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        hasMore = false;
        break;
      }

      const batch = writeBatch(db);
      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
        deletedCount++;
      });

      await batch.commit();
      console.log(`📦 배치 삭제 완료: ${collectionPath} (${snapshot.docs.length}개)`);
    }

    return deletedCount;
  }

  /**
   * 사용자 삭제 전 검증
   * @param {string} userId - 사용자 ID
   * @returns {Object} 검증 결과
   */
  async validateUserDeletion(userId) {
    try {
      // 사용자 존재 여부 확인
      const userRef = doc(db, `artifacts/${this.appId}/users/${userId}`);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        return {
          valid: false,
          message: '사용자를 찾을 수 없습니다.'
        };
      }

      const userData = userSnap.data();
      
      // 비자 만료 여부 확인
      const hasExpiredVisa = await this.checkVisaExpiry(userId);
      
      return {
        valid: true,
        user: userData,
        hasExpiredVisa: hasExpiredVisa,
        message: hasExpiredVisa 
          ? '비자 만료된 사용자입니다. 삭제가 가능합니다.'
          : '비자가 유효한 사용자입니다. 삭제를 권장하지 않습니다.'
      };

    } catch (error) {
      return {
        valid: false,
        message: `검증 중 오류가 발생했습니다: ${error.message}`
      };
    }
  }

  /**
   * 비자 만료 여부 확인
   */
  async checkVisaExpiry(userId) {
    try {
      const visaCollectionPath = `artifacts/${this.appId}/users/${userId}/documents/visa/items`;
      const visaSnapshot = await getDocs(collection(db, visaCollectionPath));
      
      if (visaSnapshot.empty) {
        return false; // 비자 문서가 없으면 만료되지 않음
      }

      const now = new Date();
      for (const doc of visaSnapshot.docs) {
        const visaData = doc.data();
        if (visaData.expiryDate) {
          const expiryDate = new Date(visaData.expiryDate);
          if (expiryDate < now) {
            return true; // 만료된 비자 발견
          }
        }
      }

      return false;
    } catch (error) {
      console.warn('비자 만료 확인 실패:', error.message);
      return false;
    }
  }
}

export default new UserDeletionService();
